import { useEffect, memo, useState } from 'react';
import cx from 'classnames';
import L from 'leaflet';
import ReactDOMServer from 'react-dom/server';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { Marker, Popup, useMapEvents, useMap } from 'react-leaflet';
import { IconMapPin } from 'Icons';
import { isObjEmpty, Names, slugify } from 'Utils';
import { setFilterMarkers, setSelectedMarker, setSidebar } from 'Actions';
import styles from 'partials/mapbox.module.scss';

export const Markers = memo(() => {
	const map = useMap();
	const history = useHistory();
	const markers = useSelector((state) => state.marker);
	const dispatch = useDispatch();
	const [selected, setSelected] = useState({});
	const isMobile = useMediaQuery({ maxWidth: 992 });
	const selectedMarker = markers.selectedMarker;

	const createClusterCustomIcon = (cluster) => {
		let childCount = cluster.getChildCount();
		const markerClass = cx(styles.marker, {
			[styles.markerBig]: childCount >= 5,
			[styles.markerSmall]: childCount < 5
		});
		return L.divIcon({
			html: ReactDOMServer.renderToString(
				<div className={markerClass}>{childCount}</div>
			),
			className: styles.markers,
			iconSize: childCount < 5 ? L.point(34, 34) : L.point(40, 40)
		});
	};

	const markerIcon = new L.divIcon({
		html: ReactDOMServer.renderToString(
			<div className={styles.single}>
				<IconMapPin className={styles.singleIcon} />
			</div>
		),
		iconSize: L.Point(24, 24),
		className: styles.markerIcon,
		popupAnchor: [18, -17]
	});

	const getMarkersInBound = (bounds) => {
		const features = markers.originList.filter((marker) => {
			return bounds?.contains(
				L.latLng(marker.coordinates.lat, marker.coordinates.lng)
			);
		});
		return features;
	};

	useMapEvents({
		moveend: () => {
			// const list = markers.originList;
			const mapBound = map.getBounds();
			const features = getMarkersInBound(mapBound);
			dispatch(setFilterMarkers(features));
		},
		click: (e) => {
			map.setView(e.latlng, map.getZoom(), {
				animate: false
			});
		}
	});

	const onSelectMarker = (marker) => {
		dispatch(setSelectedMarker(marker));
		isMobile && dispatch(setSidebar(true));
		setSelected(marker);
		history.push(
			`${Names.story.path}/${marker.id}/${slugify(marker.slug)}`
		);
	};

	const onOpenPopup = (e = null, marker) => {
		if (!isMobile && e?.target) {
			setSelected(marker);
			e.target.openPopup();
		} else {
			return null;
		}
	};

	const onClosePopup = (e) => {
		if (e.target.isPopupOpen() && !isMobile) {
			e.target.closePopup();
		}
	};

	const handleClusterClick = (cluster) => {
		map.fitBounds(cluster.layer.getBounds().pad(0.1));
	};

	useEffect(() => {
		if (!isObjEmpty(selectedMarker)) {
			map.flyTo(
				[
					selectedMarker.coordinates.lat,
					selectedMarker.coordinates.lng
				],
				17,
				{
					animate: true,
					duration: 0.75
				}
			);
		}
	}, [map, selectedMarker]);

	return (
		<MarkerClusterGroup
			animate
			spiderfyOnMaxZoom
			maxClusterRadius={40}
			showCoverageOnHover={false}
			iconCreateFunction={createClusterCustomIcon}
			eventHandlers={{
				clusterclick: (cluster) => handleClusterClick(cluster)
			}}>
			{!markers.loading &&
				markers.list?.map((marker) => {
					if (marker.coordinates.lat && marker.coordinates.lng) {
						return (
							<Marker
								riseOnHover
								icon={markerIcon}
								key={marker.id}
								position={[
									marker.coordinates.lat,
									marker.coordinates.lng
								]}
								eventHandlers={{
									click: () => onSelectMarker(marker),
									mouseover: (e) => onOpenPopup(e, marker),
									mouseout: onClosePopup
								}}>
								{!isMobile && (
									<Popup>
										<div className={styles.popup}>
											<img
												src={
													selected?.location
														?.location_avatar
												}
												alt={selected?.title}
												className={styles.popupImg}
											/>
											<div
												className={styles.popupContent}>
												<h3
													className={
														styles.popupTitle
													}>
													{selected?.location?.title}
												</h3>
											</div>
										</div>
									</Popup>
								)}
							</Marker>
						);
					}
				})}
		</MarkerClusterGroup>
	);
});

Markers.displayName = 'Markers';
